import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { GoogleMap, Marker, Circle, useJsApiLoader, Autocomplete, Libraries } from '@react-google-maps/api';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification, extractAddressComponents, getZoomForRadius, formatRand } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { IAddStoreForm, IStoreResponse, IStore } from '@/interfaces/Store/store.interface';
import { SectionCard } from '@/components/common/SectionCard';
import { MapPin, UtensilsCrossed, Image as ImageIcon, Clock } from 'lucide-react';
import MultiImageUploader from '@/components/common/MultiImageUploader';
import { CommonSelect } from '@/components/common/CommonSelect';
import { COUNTRY_CODES } from '@/utility/Country';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { fetchUserDetails } from '@/redux/features/UserSlice';
import { useAppDispatch } from '@/redux/store';

const GOOGLE_API_KEY = import.meta.env.VITE_APP_GOOGLE_API_KEY || '';

const defaultLocation = {
    lat: 28.022936,
    lng: 73.311913
};

const mapContainerStyle = {
    width: '100%',
    height: '350px',
    borderRadius: '12px'
};

const LIBRARIES: Libraries = ['places'];

const StoreProfile = () => {
    const [fetching, setFetching] = useState(true);
    const [store, setStore] = useState<IStore | null>(null);
    const [markerPosition, setMarkerPosition] = useState(defaultLocation);
    const [searchBox, setSearchBox] = useState<google.maps.places.Autocomplete | null>(null);
    const dispatch = useAppDispatch();
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_API_KEY,
        libraries: LIBRARIES
    });

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        control,
        formState: { errors, isSubmitting },
    } = useForm<IAddStoreForm>({
        defaultValues: {
            countryCode: '+27',
            commission: '0',
        }
    });

    // ─ Live radius values ─
    const deliveryRadiusVal = watch('deliveryRadius');
    const extendedDeliveryRadiusVal = watch('extendedDeliveryRadius');
    const deliveryKm = parseFloat(deliveryRadiusVal || '0') || 0;
    const extendedKm = parseFloat(extendedDeliveryRadiusVal || '0') || 0;
    const maxKm = extendedKm > 0 ? extendedKm : deliveryKm;
    const mapZoom = maxKm > 0 ? getZoomForRadius(maxKm) : 15;

    useEffect(() => {
        const fetchStoreData = async () => {
            try {
                setFetching(true);
                // Fetch manager's store profile
                const response = await callApi<{ data: IStore }>(
                    `${API_URL}${API_ENDPOINT.stores}/profile`,
                    { method: 'GET' }
                );

                if (response?.data) {
                    const storeData = response?.data;
                    setStore(storeData);

                    const lat = storeData.latitude ? parseFloat(storeData.latitude) : defaultLocation.lat;
                    const lng = storeData.longitude ? parseFloat(storeData.longitude) : defaultLocation.lng;
                    setMarkerPosition({ lat, lng });

                    // console.log(storeData.settings);


                    reset({
                        name: storeData.name || '',
                        description: storeData.description || '',
                        address: storeData.address || '',
                        contact: storeData.contact || '',
                        countryCode: storeData.countryCode || '+27',
                        latitude: lat.toString(),
                        longitude: lng.toString(),
                        // postcode: storeData.postcode || '',
                        // suburb: storeData.suburb || '',
                        // area: storeData.area || '',
                        // locality: storeData.locality || '',
                        // city: storeData.city || '',
                        // state: storeData.state || '',
                        // country: storeData.country || '',
                        storeLogo: storeData.media?.filter(m => m.referenceType === 'store_logo').map(m => `${API_URL}/${m.url}`) || [],
                        storeBanner: storeData.media?.filter(m => m.referenceType === 'store_banner').map(m => `${API_URL}/${m.url}`) || [],
                        fullName: '',
                        email: '',
                        password: '',
                        openingTime: storeData.settings?.openingTime || '09:00',
                        closingTime: storeData.settings?.closingTime || '22:00',
                        deliveryRadius: storeData.settings?.deliveryRadius || '5',
                        fixDeliveryCharge: storeData.globalSetting?.fixDeliveryCharge || '0',
                        fixCourierCharge: storeData.globalSetting?.fixCourierCharge || '0',
                        takeawayOrderStatus: storeData.settings?.takeawayOrderStatus || 'inactive',
                        deliveryOrderStatus: storeData.settings?.deliveryOrderStatus || 'inactive',
                        courierOrderStatus: storeData.settings?.courierOrderStatus || 'inactive',
                        deliveryFeeAfterRadiusPerKm: storeData.globalSetting?.deliveryFeeAfterRadiusPerKm || '',
                        extendedDeliveryRadius: storeData.settings?.extendedDeliveryRadius || '',
                        platformCharge: storeData.globalSetting?.platformCharge || '0',
                        minimumOrderValue: storeData.globalSetting?.minimumOrderValue || '0',
                        commission: storeData.settings?.commission || '0',
                        isDriverNotificationEnabled: storeData.settings?.isDriverNotificationEnabled || false,
                    } as IAddStoreForm);
                }
            } catch (error) {
                ThrowError(error);
            } finally {
                setFetching(false);
            }
        };

        fetchStoreData();
    }, [reset]);

    const onMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
            const newLat = e.latLng.lat();
            const newLng = e.latLng.lng();
            setMarkerPosition({ lat: newLat, lng: newLng });
            setValue('latitude', newLat.toString(), { shouldValidate: true });
            setValue('longitude', newLng.toString(), { shouldValidate: true });

            // Reverse geocoding to autofill address and components
            // const geocoder = new google.maps.Geocoder();
            // geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
            //     if (status === 'OK' && results && results[0]) {
            //         const place = results[0];
            //         setValue('address', place.formatted_address, { shouldValidate: true });

            //         const comps = extractAddressComponents(place.address_components);
            //         setValue('postcode', comps.postcode);
            //         setValue('suburb', comps.suburb);
            //         setValue('area', comps.area);
            //         setValue('locality', comps.locality);
            //         setValue('city', comps.city);
            //         setValue('state', comps.state);
            //         setValue('country', comps.country);
            //     }
            // });
        }
    }, [setValue]);

    const onLoadAutocomplete = (autocomplete: google.maps.places.Autocomplete) => {
        setSearchBox(autocomplete);
    };

    const onPlaceChanged = () => {
        if (searchBox !== null) {
            const place = searchBox.getPlace();
            if (place.geometry && place.geometry.location) {
                const newLat = place.geometry.location.lat();
                const newLng = place.geometry.location.lng();
                setMarkerPosition({ lat: newLat, lng: newLng });
                setValue('latitude', newLat.toString(), { shouldValidate: true });
                setValue('longitude', newLng.toString(), { shouldValidate: true });

                if (place.formatted_address) {
                    setValue('address', place.formatted_address, { shouldValidate: true });
                }

                // if (place.address_components) {
                //     const comps = extractAddressComponents(place.address_components);
                //     setValue('postcode', comps.postcode);
                //     setValue('suburb', comps.suburb);
                //     setValue('area', comps.area);
                //     setValue('locality', comps.locality);
                //     setValue('city', comps.city);
                //     setValue('state', comps.state);
                //     setValue('country', comps.country);
                // }
            }
        }
    };

    const onSubmit = async (data: IAddStoreForm) => {
        if (!store) return;
        try {
            const formData = new FormData();
            formData.append('name', data.name?.trim());
            formData.append('description', data.description?.trim() || '');
            formData.append('address', data.address?.trim());
            formData.append('contact', data.contact?.trim());
            formData.append('latitude', markerPosition.lat.toString());
            formData.append('longitude', markerPosition.lng.toString());
            formData.append('openingTime', data.openingTime);
            formData.append('closingTime', data.closingTime);
            formData.append('takeawayOrderStatus', data.takeawayOrderStatus);
            formData.append('deliveryOrderStatus', data.deliveryOrderStatus);
            formData.append('courierOrderStatus', data.courierOrderStatus);
            formData.append('deliveryRadius', data.deliveryRadius);
            formData.append('extendedDeliveryRadius', data.extendedDeliveryRadius);
            if (data.isDriverNotificationEnabled !== undefined) {
                formData.append('isDriverNotificationEnabled', String(data.isDriverNotificationEnabled));
            }

            // Append granular address components
            // formData.append('postcode', data.postcode || '');
            // formData.append('suburb', data.suburb || '');
            // formData.append('area', data.area || '');
            // formData.append('locality', data.locality || '');
            // formData.append('city', data.city || '');
            // formData.append('state', data.state || '');
            // formData.append('country', data.country || '');

            if (data.storeLogo && data.storeLogo.length > 0) {
                data.storeLogo.forEach((img) => {
                    if (typeof img !== 'string') formData.append('storeLogo', img);
                });
            }

            if (data.storeBanner && data.storeBanner.length > 0) {
                data.storeBanner.forEach((img) => {
                    if (typeof img !== 'string') formData.append('storeBanner', img);
                });
            }

            const response = await callApi<IStoreResponse>(
                `${API_URL}${API_ENDPOINT.updateStore}`,
                {
                    method: 'PUT',
                    data: formData,
                    multipart: true
                }
            );
            dispatch(fetchUserDetails());
            showToastNotification('success', response?.message || 'Store profile updated successfully');
        } catch (error) {
            ThrowError(error);
        }
    };

    if (fetching) {
        return (
            <div className="flex justify-center items-center py-12 bg-card border rounded-xl">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* ── Basic Store Info ── */}
            <SectionCard title="Basic Information" icon={<UtensilsCrossed className="w-5 h-5 text-muted-foreground" />}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                        label="Store Name"
                        name="name"
                        type="text"
                        requiredIcon
                        placeholder="e.g. My Awesome Store"
                        error={errors.name?.message}
                        {...register('name', { ...errorTypes.required('Store Name') })}
                    />
                    <div className="grid grid-cols-4 gap-4">
                        <div className="col-span-1">
                            <Controller
                                name="countryCode"
                                control={control}
                                render={({ field }) => (
                                    <div className="flex flex-col">
                                        <label className="block text-sm font-medium text-foreground mb-1.5">Code</label>
                                        <CommonSelect
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            options={COUNTRY_CODES}
                                            triggerClassName="w-full h-10 rounded-md border-border shadow-none"
                                            disabled={true}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                        <div className="col-span-3">
                            <FormField
                                label="Contact Number"
                                name="contact"
                                type="text"
                                requiredIcon
                                placeholder="123456789"
                                error={errors.contact?.message}
                                onInput={(e: React.FormEvent<HTMLInputElement>) => {
                                    const target = e.target as HTMLInputElement;
                                    target.value = target.value.replace(/[^0-9]/g, '');
                                }}
                                {...register('contact', { ...errorTypes.phone, ...errorTypes.required('Contact Number') })}
                            />
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <FormField
                            label="Store Description"
                            name="description"
                            type="textarea"
                            rows={3}
                            placeholder="Briefly describe your store..."
                            error={errors.description?.message}
                            {...register('description')}
                        />
                    </div>
                    <FormField
                        label="Commission (%)"
                        name="commission"
                        type="text"
                        disabled={true}
                        value={watch('commission')}
                        placeholder="0.00"
                    />
                </div>
            </SectionCard>

            {/* ── Store Branding (Images) ── */}
            <SectionCard title="Store Branding" icon={<ImageIcon className="w-5 h-5 text-muted-foreground" />}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <Controller
                        name="storeLogo"
                        control={control}
                        render={({ field }) => (
                            <MultiImageUploader
                                label="Store Logo"
                                value={field.value || []}
                                onChange={field.onChange}
                                maxFiles={1}
                                error={errors.storeLogo?.message}
                                type=".jpg,.jpeg,.png,.webp"
                            />
                        )}
                    />
                    <Controller
                        name="storeBanner"
                        control={control}
                        render={({ field }) => (
                            <MultiImageUploader
                                label="Store Banner"
                                value={field.value || []}
                                onChange={field.onChange}
                                maxFiles={1}
                                error={errors.storeBanner?.message}
                                type=".jpg,.jpeg,.png,.webp"
                            />
                        )}
                    />
                </div>
            </SectionCard>

            <SectionCard title="Spatial Data" icon={<MapPin className="w-5 h-5 text-muted-foreground" />}>
                <p className="text-xs text-muted-foreground mb-4">
                    Update your store's geographic coordinates. You can search for a location or drag the marker to the precise spot.
                </p>

                {isLoaded && (
                    <div className="mb-4">
                        <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
                            <input
                                type="text"
                                placeholder="Search for a location..."
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            />
                        </Autocomplete>
                    </div>
                )}

                <div className="overflow-hidden border border-secondary/20 rounded-xl bg-secondary/5 mb-4">
                    {isLoaded ? (
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={markerPosition}
                            zoom={mapZoom}
                            options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false }}
                        >
                            <Marker
                                position={markerPosition}
                                draggable={true}
                                onDragEnd={onMarkerDragEnd}
                            />
                            {/* Standard Delivery Radius — Blue */}
                            {deliveryKm > 0 && (
                                <Circle
                                    center={markerPosition}
                                    radius={deliveryKm * 1000}
                                    options={{
                                        strokeColor: '#3B82F6',
                                        strokeOpacity: 0.9,
                                        strokeWeight: 2.5,
                                        fillColor: '#3B82F6',
                                        fillOpacity: 0.13,
                                    }}
                                />
                            )}
                            {/* Extended Delivery Radius — Orange */}
                            {extendedKm > 0 && (
                                <Circle
                                    center={markerPosition}
                                    radius={extendedKm * 1000}
                                    options={{
                                        strokeColor: '#F97316',
                                        strokeOpacity: 0.9,
                                        strokeWeight: 2.5,
                                        fillColor: '#F97316',
                                        fillOpacity: 0.08,
                                    }}
                                />
                            )}
                        </GoogleMap>
                    ) : (
                        <div className="flex items-center justify-center w-full h-[350px] bg-secondary/10">
                            <span className="text-sm text-muted-foreground">Loading Maps...</span>
                        </div>
                    )}
                </div>

                {/* Radius legend — only shows when at least one radius is set */}
                {(deliveryKm > 0 || extendedKm > 0) && (
                    <div className="flex flex-wrap items-center gap-5 mb-4 p-3 bg-secondary/10 rounded-xl border border-border/40">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Coverage zones:</span>
                        {deliveryKm > 0 && (
                            <span className="flex items-center gap-2 text-sm">
                                <span className="inline-block w-3.5 h-3.5 rounded-full border-2" style={{ borderColor: '#3B82F6', backgroundColor: 'rgba(59,130,246,0.18)' }} />
                                <span className="font-semibold text-blue-600">{deliveryKm} km</span>
                                <span className="text-muted-foreground">standard zone</span>
                            </span>
                        )}
                        {extendedKm > 0 && (
                            <span className="flex items-center gap-2 text-sm">
                                <span className="inline-block w-3.5 h-3.5 rounded-full border-2" style={{ borderColor: '#F97316', backgroundColor: 'rgba(249,115,22,0.12)' }} />
                                <span className="font-semibold text-orange-500">{extendedKm} km</span>
                                <span className="text-muted-foreground">extended zone</span>
                            </span>
                        )}
                    </div>
                )}

                <FormField
                    label="Physical Address"
                    name="address"
                    type="textarea"
                    rows={3}
                    requiredIcon
                    placeholder="Enter complete store address"
                    error={errors.address?.message}
                    {...register('address', { ...errorTypes.required('Address') })}
                />
            </SectionCard>

            {/* ── Operational Settings ── */}
            <SectionCard title="Operational Settings" icon={<Clock className="w-5 h-5 text-muted-foreground" />}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <FormField
                        label="Opening Time"
                        name="openingTime"
                        type="time"
                        requiredIcon
                        error={errors.openingTime?.message}
                        {...register('openingTime', { ...errorTypes.required('Opening Time') })}
                    />
                    <FormField
                        label="Closing Time"
                        name="closingTime"
                        type="time"
                        requiredIcon
                        error={errors.closingTime?.message}
                        {...register('closingTime', { ...errorTypes.required('Closing Time') })}
                    />
                    <FormField
                        label="Delivery Radius (km)"
                        name="deliveryRadius"
                        type="text"
                        disabled={true}
                        value={watch('deliveryRadius')}
                        placeholder="e.g. 5"
                    />
                    <FormField
                        label="Extended Delivery Radius (km)"
                        name="extendedDeliveryRadius"
                        type="number"
                        min={0}
                        placeholder="e.g. 5"
                        error={errors.extendedDeliveryRadius?.message}
                        {...register('extendedDeliveryRadius', { ...errorTypes.required('Extended Radius') })}
                        onWheel={(e) => e.currentTarget.blur()}
                    />
                </div>
            </SectionCard>

            {/* ── Logistics & Order Settings ── */}
            <SectionCard title="Logistics & Order Settings" icon={<UtensilsCrossed className="w-5 h-5 text-muted-foreground" />}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Status Toogles */}
                    <div className="space-y-6 md:col-span-2">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/5">
                                <div className="space-y-0.5">
                                    <Label className="text-sm font-medium">Take Away Status</Label>
                                    <p className="text-xs text-muted-foreground">Enable take away orders</p>
                                </div>
                                <Controller
                                    name="takeawayOrderStatus"
                                    control={control}
                                    render={({ field }) => (
                                        <Switch
                                            checked={field.value === 'active'}
                                            onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'inactive')}
                                        />
                                    )}
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/5">
                                <div className="space-y-0.5">
                                    <Label className="text-sm font-medium">Delivery Status</Label>
                                    <p className="text-xs text-muted-foreground">Enable home delivery</p>
                                </div>
                                <Controller
                                    name="deliveryOrderStatus"
                                    control={control}
                                    render={({ field }) => (
                                        <Switch
                                            checked={field.value === 'active'}
                                            onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'inactive')}
                                        />
                                    )}
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/5">
                                <div className="space-y-0.5">
                                    <Label className="text-sm font-medium">Courier Status</Label>
                                    <p className="text-xs text-muted-foreground">Enable courier service</p>
                                </div>
                                <Controller
                                    name="courierOrderStatus"
                                    control={control}
                                    render={({ field }) => (
                                        <Switch
                                            checked={field.value === 'active'}
                                            onCheckedChange={(checked) => field.onChange(checked ? 'active' : 'inactive')}
                                        />
                                    )}
                                />
                            </div>

                            <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/5">
                                <div className="space-y-0.5">
                                    <Label className="text-sm font-medium">Driver Notifications</Label>
                                    <p className="text-xs text-muted-foreground">Notify drivers for new orders</p>
                                </div>
                                <Controller
                                    name="isDriverNotificationEnabled"
                                    control={control}
                                    render={({ field }) => (
                                        <Switch
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Conditional Charge Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:col-span-2">
                        {watch('deliveryOrderStatus') === 'active' && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground">Store Delivery Charge</label>
                                <div className="h-10 flex items-center px-3 border border-transparent bg-secondary/10 rounded-md text-sm text-foreground/80 font-medium">
                                    {formatRand(Number(watch('fixDeliveryCharge') || 0))}
                                </div>
                            </div>
                        )}
                        {watch('courierOrderStatus') === 'active' && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground">Store Courier Charge</label>
                                <div className="h-10 flex items-center px-3 border border-transparent bg-secondary/10 rounded-md text-sm text-foreground/80 font-medium">
                                    {formatRand(Number(watch('fixCourierCharge') || 0))}
                                </div>
                            </div>
                        )}
                        {watch('deliveryOrderStatus') === 'active' && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-foreground">Delivery Fee After Radius (Per Km)</label>
                                <div className="h-10 flex items-center px-3 border border-transparent bg-secondary/10 rounded-md text-sm text-foreground/80 font-medium">
                                    {formatRand(Number(watch('deliveryFeeAfterRadiusPerKm') || 0))} / km
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">Platform Charge</label>
                            <div className="h-10 flex items-center px-3 border border-transparent bg-secondary/10 rounded-md text-sm text-foreground/80 font-medium">
                                {formatRand(Number(watch('platformCharge') || 0))}
                            </div>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground">Minimum Order Value</label>
                            <div className="h-10 flex items-center px-3 border border-transparent bg-secondary/10 rounded-md text-sm text-foreground/80 font-medium">
                                {formatRand(Number(watch('minimumOrderValue') || 0))}
                            </div>
                        </div>
                    </div>
                </div>
            </SectionCard>

            <div className="flex justify-end pt-4">
                <Button type="submit" isLoading={isSubmitting} loadingText="Saving Changes..." className="px-10 h-11 text-base">
                    Save Store Profile
                </Button>
            </div>
        </form>
    );
};

export default StoreProfile;
